const AUTH_REDIRECT_REASON_KEY = "solarent_auth_redirect_reason";

const PUBLIC_AUTH_URLS = [
  "account/login",
  "account/signup",
  "account/RefreshToken",
  "Account/LoginKind",
  "account/VerifyRequest",
  "account/CheckVerifyCode",
  "account/ForgotPassword",
];

export function getAccessToken(user) {
  if (!user) {
    return null;
  }
  return user.accessToken || user.AccessToken || null;
}

export function getRefreshToken(user) {
  if (!user) {
    return null;
  }
  return user.refreshToken || user.RefreshToken || null;
}

/** Normalize API user payload (PascalCase or camelCase) for frontend use. */
export function normalizeAuthUser(user) {
  if (!user) {
    return null;
  }

  const accessToken = getAccessToken(user);
  if (!accessToken) {
    return null;
  }

  return {
    ...user,
    accessToken,
    refreshToken: getRefreshToken(user) || "",
    defaultUrl: user.defaultUrl || user.DefaultUrl || "",
    isShowMainMenu: user.isShowMainMenu ?? user.IsShowMainMenu ?? true,
    isProfileCompleted:
      user.isProfileCompleted ?? user.IsProfileCompleted ?? false,
    isProfileForce: user.isProfileForce ?? user.IsProfileForce ?? false,
    isConfirmed: user.isConfirmed ?? user.IsConfirmed ?? false,
    userName: user.userName || user.UserName || "",
    firstName: user.firstName || user.FirstName || "",
    lastName: user.lastName || user.LastName || "",
    roleId: user.roleId ?? user.RoleId ?? null,
    id: user.id ?? user.Id ?? null,
  };
}

export function persistAuthUser(user) {
  const normalized = normalizeAuthUser(user);
  if (!normalized) {
    return null;
  }

  localStorage.setItem("user", JSON.stringify(normalized));

  if (typeof normalized.isShowMainMenu !== "undefined") {
    localStorage.setItem(
      "isShowMainMenu",
      String(normalized.isShowMainMenu)
    );
  }

  if (normalized.defaultUrl) {
    localStorage.setItem("defaultUrl", normalized.defaultUrl);
  }

  return normalized;
}

export function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      return null;
    }
    return normalizeAuthUser(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearStoredAuth() {
  localStorage.removeItem("user");
  localStorage.removeItem("defaultUrl");
  localStorage.removeItem("isShowMainMenu");
  localStorage.removeItem("currentUserId");
  try {
    sessionStorage.removeItem("solarent_impersonation_original");
  } catch {
    // ignore
  }
}

export function isAuthRoute(pathname = window.location.pathname) {
  return String(pathname || "").startsWith("/auth");
}

export function isPublicAuthApiUrl(url = "") {
  const normalized = String(url || "").replace(/^\//, "");
  return PUBLIC_AUTH_URLS.some(
    (entry) => normalized === entry || normalized.startsWith(`${entry}?`)
  );
}

export function parseJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function isAccessTokenExpired(user, skewMs = 30_000) {
  const token = getAccessToken(user);
  if (!token) {
    return true;
  }

  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return Date.now() >= payload.exp * 1000 - skewMs;
}

export function rememberAuthRedirectReason(reason) {
  try {
    sessionStorage.setItem(
      AUTH_REDIRECT_REASON_KEY,
      JSON.stringify({
        at: new Date().toISOString(),
        ...reason,
      })
    );
  } catch {
    // ignore storage errors
  }
}

export function consumeAuthRedirectReason() {
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_REASON_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_REASON_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function redirectToLogin(reason = {}) {
  clearStoredAuth();
  rememberAuthRedirectReason(reason);

  if (isAuthRoute()) {
    return;
  }

  window.location.href = "/auth/login";
}

const isInvalidPostLoginFrom = (from) =>
  !from ||
  from === "/" ||
  from === "/403" ||
  String(from).startsWith("/auth");

export const isMainMenuHiddenForUser = (user) => {
  const value = user?.isShowMainMenu ?? user?.IsShowMainMenu;
  if (value === false || value === "false") {
    return true;
  }
  if (value === true || value === "true") {
    return false;
  }
  return localStorage.getItem("isShowMainMenu") === "false";
};

export const getUserDefaultUrl = (user) => {
  const fromUser = user?.defaultUrl || user?.DefaultUrl || "";
  const trimmed = String(fromUser || "").trim();
  if (trimmed) {
    return trimmed;
  }
  return String(localStorage.getItem("defaultUrl") || "").trim();
};

/** Resolve landing page after login (respects profile gate + role defaultUrl). */
export function resolvePostLoginUrl(from, user) {
  if (user?.isProfileForce && !user?.isConfirmed) {
    return "/account/profile";
  }

  if (!isInvalidPostLoginFrom(from)) {
    return from;
  }

  const roles = user?.roleNames ?? user?.RoleNames ?? [];
  const roleList = Array.isArray(roles) ? roles.map((r) => String(r).toLowerCase()) : [];
  const isMember =
    roleList.includes("contractormember") && !roleList.includes("contractor");
  if (isMember) {
    return "/SLN/Contractor/AssistedRegistration";
  }

  const defaultUrl = getUserDefaultUrl(user);
  if (defaultUrl && isMainMenuHiddenForUser(user)) {
    return defaultUrl;
  }

  return "/";
}
